/* eslint-disable no-unused-vars */
import React from 'react';
import { Box, Flex, Text, Icon } from '@chakra-ui/react';
import { User, CurrencyDollarSimple, Buildings } from 'phosphor-react';
import { IPartner } from '../../../../../hooks/useCompanies';
import { formatCPF, formatCNPJ, toCurrency } from '../../../../../utils/FormatFunctions';

interface PartnerCardProps {
  partner: IPartner & {
    empresas_simples?: number;
    empresas_presumido?: number;
  };
  onClick: (partner: IPartner) => void;
}

const formatCPForCNPJ = (value: string | number | null | undefined): string => {
  if (!value) return '';
  const cleanValue = value.toString().replace(/\D/g, '');

  // CNPJ tem 14 dígitos, CPF tem 11 dígitos
  if (cleanValue.length === 14) {
    return formatCNPJ(value);
  }
  return formatCPF(value);
};

export function PartnerCard({ partner, onClick }: PartnerCardProps) {
  const empresasSimples = partner.empresas_simples ?? 0;
  const empresasPresumido = partner.empresas_presumido ?? 0;
  const totalEmpresas = empresasSimples + empresasPresumido;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      p={4}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={() => onClick(partner)}
    >
      {/* Header com CPF/CNPJ */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" fontWeight="bold" color="gray.600" fontFamily="mono">
          {formatCPForCNPJ(partner.inscricao)}
        </Text>
      </Flex>

      {/* Nome do Sócio */}
      <Flex align="center" gap={2} mb={3}>
        <Icon as={User} color="blue.500" boxSize={5} />
        <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={2}>
          {partner.nome_socio}
        </Text>
      </Flex>

      {/* Grid de informações */}
      <Box borderTop="1px" borderColor="gray.100" pt={3}>
        <Flex wrap="wrap" gap={3} direction="column">
          {/* Total de Empresas */}
          <Flex align="center" gap={2} bg="blue.50" px={2} py={1} borderRadius="md">
            <Icon as={Buildings} color="blue.500" boxSize={3} />
            <Text fontSize="xs" color="blue.700" fontWeight="medium">
              Total: {totalEmpresas} empresas
            </Text>
          </Flex>

          {/* Empresas Simples Nacional */}
          {empresasSimples > 0 && (
            <Flex align="center" gap={2} bg="green.50" px={2} py={1} borderRadius="md">
              <Icon as={Buildings} color="green.500" boxSize={3} />
              <Text fontSize="xs" color="green.700" fontWeight="medium">
                Simples Nacional: {empresasSimples}
              </Text>
            </Flex>
          )}

          {/* Empresas Lucro Presumido */}
          {empresasPresumido > 0 && (
            <Flex align="center" gap={2} bg="purple.50" px={2} py={1} borderRadius="md">
              <Icon as={Buildings} color="purple.500" boxSize={3} />
              <Text fontSize="xs" color="purple.700" fontWeight="medium">
                Lucro Presumido: {empresasPresumido}
              </Text>
            </Flex>
          )}

          {/* Faturamento Acumulado */}
          <Flex align="center" gap={2} bg="orange.50" px={2} py={1} borderRadius="md">
            <Icon as={CurrencyDollarSimple} color="orange.500" boxSize={3} />
            <Text fontSize="xs" color="orange.700" fontWeight="medium">
              {toCurrency(partner.faturamento_acumulado || 0)}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
